'use client'

import { usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { getVendorPhones } from "@/utils/vendorPhones"
import { useNavigation } from "@/lib/commerce/hooks"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const sweepLink = `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[100%_1px] hover:bg-size-[0%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`
const valueLink = `pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`

function ContactRow({ label, value, href, action, note, external }: { label: string, value: string, href: string, action: string, note?: string, external?: boolean }) {
  const linkProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {}
  return (
    <li className="py-8 border-b border-border grid grid-cols-1 sm:grid-cols-[180px_minmax(0,1fr)_auto] gap-x-10 gap-y-3 sm:items-baseline">
      <span className={eyebrow}>{label}</span>
      <div className="min-w-0">
        <a href={href} {...linkProps} className={`text-xl sm:text-2xl tracking-tight text-foreground wrap-break-word ${valueLink}`}>{value}</a>
        {note && <p className="mt-2 text-[13px] text-muted-foreground">{note}</p>}
      </div>
      <a href={href} {...linkProps} className={`w-fit text-[13px] ${sweepLink}`}>{action}</a>
    </li>
  )
}

export default function Contact({ vendor }: { vendor?: any }) {
  const pathname = usePathname()
  const phones = getVendorPhones(vendor)
  const { footer } = useNavigation({ vendor })
  const hasBranches = vendor?.modules?.includes('branch')

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${focusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
      </nav>

      <header className="mt-8 lg:mt-16 max-w-3xl">
        <div className={eyebrow}>Холбоо барих</div>
        <h1 className="mt-4 text-4xl sm:text-6xl tracking-tight text-foreground">Бидэнтэй холбогдох</h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">Бүтээгдэхүүн, захиалга, хүргэлттэй холбоотой асуултад утас, имэйлээр эсвэл биечлэн дэлгүүрт ирж хариу авах боломжтой.</p>
      </header>

      <ul className="mt-16 lg:mt-24 border-t border-border">
        {phones.map(p => <ContactRow key={p} label="Утас" value={p} href={`tel:${p}`} action="Залгах" />)}
        {vendor?.email && <ContactRow label="Имэйл" value={vendor.email} href={`mailto:${vendor.email}`} action="Имэйл илгээх" />}
        {vendor?.address && (
          <ContactRow
            label="Хаяг"
            value={vendor.address}
            note={vendor?.addressLandmark || undefined}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`}
            action="Газрын зураг"
            external
          />
        )}
      </ul>

      {(footer.socials.length > 0 || hasBranches) && (
        <div className="mt-12 flex flex-col sm:flex-row sm:items-baseline gap-x-10 gap-y-4">
          {footer.socials.length > 0 && (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-[13px]">
              <span className={`w-45 ${eyebrow}`}>Сошиал</span>
              {footer.socials.map(s => <a key={s.platform} href={s.href} target="_blank" rel="noopener noreferrer" className={sweepLink}>{s.name}</a>)}
            </div>
          )}
          {hasBranches && (
            <a href={navigateUrl('/branch', pathname)} className={`sm:ml-auto w-fit text-[13px] ${sweepLink}`}>Салбаруудыг үзэх</a>
          )}
        </div>
      )}
    </div>
  )
}
